import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { en } from "@/i18n/locales/en";
import { sq } from "@/i18n/locales/sq";
import { sr } from "@/i18n/locales/sr";
import { tr } from "@/i18n/locales/tr";

export type AppLanguage = "sq" | "en" | "sr" | "tr";

const storageKey = "eshendetesia.language";

type TranslationValue = string | TranslationTree;
type TranslationTree = { [key: string]: TranslationValue };

const isTranslationTree = (value: TranslationValue | undefined): value is TranslationTree =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Keep incomplete locale files usable while their native translations are added.
 * A locale's own text always wins; only absent or empty values inherit Albanian.
 */
const withAlbanianFallback = (
  fallback: TranslationTree,
  locale: TranslationTree,
): TranslationTree => {
  const merged: TranslationTree = { ...fallback };

  for (const [key, value] of Object.entries(locale)) {
    const fallbackValue = fallback[key];
    merged[key] = isTranslationTree(fallbackValue) && isTranslationTree(value)
      ? withAlbanianFallback(fallbackValue, value)
      : value;
  }

  return merged;
};

const srWithFallback = withAlbanianFallback(sq as TranslationTree, sr as TranslationTree);
const trWithFallback = withAlbanianFallback(sq as TranslationTree, tr as TranslationTree);

const readInitialLanguage = (): AppLanguage => {
  if (typeof window === "undefined") {
    return "sq";
  }

  const stored = window.localStorage.getItem(storageKey);
  if (stored === "sq" || stored === "en" || stored === "sr" || stored === "tr") {
    return stored;
  }

  return "sq";
};

void i18n.use(initReactI18next).init({
  resources: {
    sq: { translation: sq },
    en: { translation: en },
    sr: { translation: srWithFallback },
    tr: { translation: trWithFallback },
  },
  lng: readInitialLanguage(),
  fallbackLng: "sq",
  returnEmptyString: false,
  returnNull: false,
  interpolation: {
    escapeValue: false,
  },
});

i18n.on("languageChanged", (language) => {
  if (language === "sq" || language === "en" || language === "sr" || language === "tr") {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, language);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }
});

if (typeof document !== "undefined") {
  document.documentElement.lang = readInitialLanguage();
}

export { i18n };
